import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useBrandManager } from './useBrandManager';
import { useUserContext, UserContext } from './useUserContext';
import { usePageVisibility } from './usePageVisibility';
import { toast } from './use-toast';

interface SupportThread {
  id: string;
  brand_id: string;
  user_id: string;
  subject: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  user_context: UserContext;
  created_at: string;
  updated_at: string;
  last_message_at: string;
}

interface SupportMessage {
  id: string;
  thread_id: string;
  user_id: string;
  message: string;
  is_internal_note: boolean;
  attachments: any[];
  created_at: string;
  read_at?: string | null;
  profiles?: {
    full_name: string | null;
    email: string | null;
  };
}

export const useSupportChat = (threadId?: string) => {
  const { user } = useAuth();
  const { currentBrandId } = useBrandManager();
  const { captureContext } = useUserContext();
  const { isVisible } = usePageVisibility();
  const queryClient = useQueryClient();

  // Fetch user's threads (all threads for current brand, not just user's)
  const { data: threads, isLoading: threadsLoading } = useQuery({
    queryKey: ['support-threads', user?.id, currentBrandId],
    queryFn: async () => {
      if (!user?.id || !currentBrandId) return [];

      const { data, error } = await supabase
        .from('support_threads')
        .select('*')
        .eq('brand_id', currentBrandId)
        .order('last_message_at', { ascending: false });

      if (error) throw error;
      return (data || []) as unknown as SupportThread[];
    },
    enabled: !!user?.id && !!currentBrandId,
  });

  // Fetch messages for a specific thread
  const { data: messages, isLoading: messagesLoading } = useQuery({
    queryKey: ['support-messages', threadId],
    queryFn: async () => {
      if (!threadId) return [];

      const { data, error } = await supabase
        .from('support_messages')
        .select('*')
        .eq('thread_id', threadId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      
      // Fetch profile data separately for each message
      const messagesWithProfiles = await Promise.all(
        (data || []).map(async (msg) => {
          const { data: profile } = await supabase
            .from('profiles')
            .select('full_name, email')
            .eq('id', msg.user_id)
            .single();
          
          return {
            ...msg,
            profiles: profile || undefined,
          };
        })
      );

      return messagesWithProfiles as unknown as SupportMessage[];
    },
    enabled: !!threadId,
  });

  // Create new thread
  const createThreadMutation = useMutation({
    mutationFn: async (subject: string) => {
      if (!user?.id || !currentBrandId) {
        throw new Error('User or brand not found');
      }

      const userContext = captureContext();

      const { data, error } = await supabase
        .from('support_threads')
        .insert([{
          brand_id: currentBrandId,
          user_id: user.id,
          subject,
          user_context: userContext as any,
        }])
        .select()
        .single();

      if (error) throw error;
      
      // Trigger auto-categorization for new thread
      try {
        await supabase.functions.invoke('support-auto-categorize', {
          body: {
            threadId: data.id,
            subject,
            messages: [],
          },
        });
      } catch (e) {
        console.error('Auto-categorization failed:', e);
        // Don't fail thread creation if categorization fails
      }
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-threads'] });
      toast({
        title: 'Support thread created',
        description: 'Your support request has been submitted.',
      });
    },
    onError: (error) => {
      console.error('Error creating thread:', error);
      toast({
        title: 'Error',
        description: 'Failed to create support thread. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Send message
  const sendMessageMutation = useMutation({
    mutationFn: async ({ 
      threadId, 
      message,
      attachments = [] 
    }: { 
      threadId: string; 
      message: string;
      attachments?: Array<{ name: string; url: string; size: number; type: string }>;
    }) => {
      if (!user?.id) throw new Error('User not found');

      const { data, error } = await supabase
        .from('support_messages')
        .insert({
          thread_id: threadId,
          user_id: user.id,
          message,
          is_internal_note: false,
          attachments,
        })
        .select()
        .single();

      if (error) throw error;
      
      // Trigger email notification
      try {
        await supabase.functions.invoke('send-support-notification', {
          body: {
            type: 'new_message',
            thread_id: threadId,
            message_id: data.id,
          },
        });
      } catch (emailError) {
        console.error('Failed to send email notification:', emailError);
        // Don't fail the message send if email fails
      }
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-messages'] });
      queryClient.invalidateQueries({ queryKey: ['support-threads'] });
    },
    onError: (error) => {
      console.error('Error sending message:', error);
      toast({
        title: 'Error',
        description: 'Failed to send message. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Real-time subscription for threads (visibility-aware)
  useEffect(() => {
    if (!currentBrandId || !user?.id || !isVisible) return;

    const threadsChannel = supabase
      .channel(`support-threads-${currentBrandId}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'support_threads',
          filter: `brand_id=eq.${currentBrandId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['support-threads', user.id, currentBrandId] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(threadsChannel);
    };
  }, [currentBrandId, user?.id, queryClient, isVisible]);

  // Real-time subscription for messages (visibility-aware)
  useEffect(() => {
    if (!threadId || !isVisible) return;

    const messagesChannel = supabase
      .channel(`support-messages-${threadId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'support_messages',
          filter: `thread_id=eq.${threadId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['support-messages', threadId] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(messagesChannel);
    };
  }, [threadId, queryClient, isVisible]);

  return {
    threads,
    messages,
    threadsLoading,
    messagesLoading,
    createThread: createThreadMutation.mutate,
    sendMessage: sendMessageMutation.mutate,
    isCreatingThread: createThreadMutation.isPending,
    isSendingMessage: sendMessageMutation.isPending,
  };
};
